// Platform-neutral icon token; mobile's GlassChatBar and the web chip row
// each map these to their own icon assets.
export type T_ShellSuggestionIcon =
  | "bell"
  | "calendar"
  | "globe"
  | "list"
  | "market"
  | "portfolio"
  | "sparkle";

export const E_ShellSuggestionPlacement = {
  COMPOSER_FOCUS: "composer_focus",
  HOME_CHIPS: "home_chips",
} as const;

export type T_ShellSuggestionPlacement =
  (typeof E_ShellSuggestionPlacement)[keyof typeof E_ShellSuggestionPlacement];

export type T_ShellChatSurface = "conversation" | "home";

export type T_ShellSuggestionDefinition = {
  icon: T_ShellSuggestionIcon;
  id: string;
  text: string;
};

export type T_ShellSuggestionAnalyticsContext = {
  chatSurface: T_ShellChatSurface;
  placement: T_ShellSuggestionPlacement;
};

export const SHELL_SUGGESTIONS_BY_ID = {
  alerts_view: {
    icon: "bell",
    id: "alerts_view",
    text: "Show me my alerts",
  },
  analyze_stock: {
    icon: "market",
    id: "analyze_stock",
    text: "Analyze a stock",
  },
  holdings_view: {
    icon: "list",
    id: "holdings_view",
    text: "Show me my holdings",
  },
  news_feed: {
    icon: "globe",
    id: "news_feed",
    text: "What's new today?",
  },
  portfolio_concentration: {
    icon: "portfolio",
    id: "portfolio_concentration",
    text: "Am I too concentrated?",
  },
  portfolio_events: {
    icon: "calendar",
    id: "portfolio_events",
    text: "What events could move my portfolio?",
  },
  portfolio_move: {
    icon: "market",
    id: "portfolio_move",
    text: "Why did my portfolio move today?",
  },
  portfolio_rating: {
    icon: "sparkle",
    id: "portfolio_rating",
    text: "Rate my portfolio",
  },
} as const satisfies Record<string, T_ShellSuggestionDefinition>;

// These ordered placements reference the canonical definitions above so copy,
// IDs, and icons cannot drift between the rendered question and its analytics.
export const SHELL_HOME_SUGGESTIONS = [
  SHELL_SUGGESTIONS_BY_ID.portfolio_rating,
  SHELL_SUGGESTIONS_BY_ID.analyze_stock,
  SHELL_SUGGESTIONS_BY_ID.portfolio_move,
  SHELL_SUGGESTIONS_BY_ID.portfolio_events,
  SHELL_SUGGESTIONS_BY_ID.portfolio_concentration,
] as const;

// Every scripted prompt, ordered scripted-flows first (deterministic
// journeys), then the model-routed asks. The composer-focus surface renders
// this full list scrollably.
export const SHELL_COMPOSER_SUGGESTIONS = [
  SHELL_SUGGESTIONS_BY_ID.portfolio_rating,
  SHELL_SUGGESTIONS_BY_ID.portfolio_move,
  SHELL_SUGGESTIONS_BY_ID.portfolio_events,
  SHELL_SUGGESTIONS_BY_ID.analyze_stock,
  SHELL_SUGGESTIONS_BY_ID.holdings_view,
  SHELL_SUGGESTIONS_BY_ID.news_feed,
  SHELL_SUGGESTIONS_BY_ID.alerts_view,
  SHELL_SUGGESTIONS_BY_ID.portfolio_concentration,
] as const;

export function buildPremadeQuestionsShownProperties(
  context: T_ShellSuggestionAnalyticsContext,
  suggestions: readonly T_ShellSuggestionDefinition[],
) {
  return {
    chat_surface: context.chatSurface,
    placement: context.placement,
    question_count: suggestions.length,
    question_ids: suggestions.map((suggestion) => suggestion.id),
  };
}

export function buildPremadeQuestionTappedProperties(
  context: T_ShellSuggestionAnalyticsContext,
  suggestion: T_ShellSuggestionDefinition,
  questionIndex: number,
) {
  return {
    chat_surface: context.chatSurface,
    placement: context.placement,
    question_id: suggestion.id,
    question_index: questionIndex,
  };
}
